import { Col, Form, Row } from "react-bootstrap";
import { PPA_TYPE_LABELS } from "./enums";
import { parseAsNumber } from "./utils";

import { useOrganisationList } from "@inrange/building-manager-api-client";
import {
  ChangeToSiteDefaults,
  getPpas,
  ppaErrorString,
  ppaStartingTariff,
  ppaUpdate,
  RecursivePartialSiteAllowStringValues,
  SiteAllowStringValues,
} from "@inrange/building-manager-api-client/models-site";
import {
  SiteCalculationsContext,
  useContextTS,
} from "@inrange/shared-components";
import { BorderedCheckbox, Modal, ModalView } from "@inrange/theme-components";
import { currencySymbol } from "@inrange/theme-components/formatting";
import { Icons } from "@inrange/theme-components/icons";
import { useState } from "react";
import Button from "react-bootstrap/Button";
import FormInputPercentage from "../../FormInputPercentage";
import CollapsibleCard from "../CollapsibleCard";
import SiteInvoiceProps from "./SiteInvoiceProps";

const DealTerms = ({
  site,
  setSite,
  setPendingUpdateSite,
  isOwnerOccupied,
  isBulkEdit,
}: {
  site: SiteAllowStringValues;
  setSite: (site: RecursivePartialSiteAllowStringValues) => void;
  setPendingUpdateSite: (updates: ChangeToSiteDefaults[] | undefined) => void;
  isOwnerOccupied: boolean;
  isBulkEdit?: boolean;
}) => {
  const { errors, siteCalculations } = useContextTS(SiteCalculationsContext);
  const { fetchOrganisations } = useOrganisationList();
  const [suggestedOnsitePpaLength, setSuggestedOnsitePpaLength] = useState<
    | {
        leaseMonthsLeft: number;
        suggestedPpaLength: number;
      }
    | undefined
  >(undefined);
  const orgsById = (fetchOrganisations.data?.organisations || []).reduce(
    (acc, org) => {
      acc[org.id] = org;
      return acc;
    },
    {}
  );
  const maxTenantTariff = (site.siteOwnerships || [])
    .filter(
      (so) =>
        (so.ownership === "tenant" || so.ownership === "ownerOccupier") &&
        orgsById[so.orgID] !== undefined &&
        orgsById[so.orgID].maxTenantTariff !== undefined
    )
    .map((so) => orgsById[so.orgID].maxTenantTariff)
    .reduce((acc, maxTenantTariff) => {
      if (acc === undefined) {
        return maxTenantTariff;
      }
      // If there are multiple maxTenantTariff values, return the lowest one
      return maxTenantTariff < acc ? maxTenantTariff : acc;
    }, undefined);
  const ppaTermFromLeaseLength = (leaseLengthDate: string) => {
    const leaseLengthDateObj = new Date(leaseLengthDate);
    const now = new Date();
    const diffYears =
      leaseLengthDateObj.getUTCFullYear() - now.getUTCFullYear();
    const diffMonths = leaseLengthDateObj.getUTCMonth() - now.getUTCMonth();
    const diffDays = leaseLengthDateObj.getUTCDate() - now.getUTCDate();

    const leaseMonthsLeft = Math.max(
      diffYears * 12 + diffMonths + Math.ceil(diffDays / 30),
      0
    );
    const suggestedPpaLength = Math.min(
      Math.max(3, Math.floor(leaseMonthsLeft / 12)),
      10
    );
    return {
      leaseMonthsLeft,
      suggestedPpaLength,
    };
  };
  return (
    <CollapsibleCard
      title={"Deal and contract terms"}
      className="mt-2"
      isBulkEdit={isBulkEdit}
    >
      {getPpas(site.tariffConfig.onsitePPA, "other").map((ppa, index) => (
        <Row
          className="d-flex flex-wrap align-items-end"
          key={`onsite-ppa-${index}`}
        >
          <Col sm={4}>
            <Form.Group
              className="mb-3"
              controlId={`onsitePpaTariff-${index}`}
              style={{
                display: "grid",
                gridTemplateColumns:
                  !ppa.nextPpa && index > 0
                    ? "auto 32px 32px"
                    : !ppa.nextPpa && index == 0
                      ? "auto 32px"
                      : "auto",
                gridTemplateRows: "auto auto auto",
                gap: "0 8px",
              }}
            >
              <Form.Label
                style={{
                  gridRow: 1,
                  gridColumn:
                    !ppa.nextPpa && index > 0
                      ? "span 3"
                      : !ppa.nextPpa && index == 0
                        ? "span 2"
                        : 1,
                }}
              >
                {site.tariffConfig.onsitePPA?.ppaPurchaseModel !==
                  "fixed_lease" && (
                  <>
                    {parseFloat(site.totalInstalledCapacity as string) === 0
                      ? "Wired"
                      : "Tenant"}{" "}
                    tariff{index > 0 && ` ${index + 1}`}
                  </>
                )}
                {site.tariffConfig.onsitePPA?.ppaPurchaseModel ===
                  "fixed_lease" && (
                  <>
                    Annual charge (
                    {currencySymbol(siteCalculations.currencyCode)})
                    {index > 0 && ` ${index + 1}`}
                  </>
                )}
              </Form.Label>
              <Form.Control
                type="text"
                style={{ gridRow: 2, gridColumn: 1 }}
                value={ppa.tariff}
                onChange={(e) =>
                  setSite({
                    tariffConfig: {
                      onsitePPA: ppaUpdate(index, { tariff: e.target.value }),
                    },
                  })
                }
                isInvalid={
                  !!errors[
                    `tariffConfig.onsitePPA${ppaErrorString(index)}.tariff`
                  ] ||
                  (maxTenantTariff &&
                    parseAsNumber(ppa.tariff) > maxTenantTariff)
                }
              />
              <Form.Control.Feedback
                type="invalid"
                style={{ gridRow: 3, gridColumn: 1 }}
              >
                {errors[
                  `tariffConfig.onsitePPA${ppaErrorString(index)}.tariff`
                ] ||
                  (maxTenantTariff &&
                  parseAsNumber(ppa.tariff) > maxTenantTariff
                    ? `Max tenant tariff is ${maxTenantTariff}`
                    : undefined)}
              </Form.Control.Feedback>
              {!ppa.nextPpa && (
                <Button
                  style={{ gridRow: 2, gridColumn: index === 0 ? 2 : 3 }}
                  variant="success"
                  data-testid={"onsite-ppa-add"}
                  onClick={() =>
                    setSite({
                      tariffConfig: {
                        onsitePPA: ppaUpdate(index, {
                          nextPpa: {
                            tariff: ppaStartingTariff(
                              parseAsNumber(ppa.tariff),
                              parseAsNumber(ppa.ppaLength),
                              parseAsNumber(site.energyPriceInflationRate),
                              site.tariffConfig.onsitePPA
                                ?.ppaCrossPpaIndexationType ?? "compound"
                            ),
                            ppaContractType: ppa.ppaContractType || "other",
                            ppaLength: ppa.ppaLength,
                            ppaType: ppa.ppaType || "fixed",
                            ppaIndex: ppa.ppaIndex,
                            nextPpa: undefined,
                          },
                        }),
                      },
                    })
                  }
                >
                  +
                </Button>
              )}
              {index > 0 && !ppa.nextPpa && (
                <div
                  onClick={() => {
                    setSite({
                      tariffConfig: {
                        onsitePPA: ppaUpdate(index - 1, {
                          nextPpa: null as unknown as undefined,
                        }),
                      },
                    });
                  }}
                  style={{
                    gridRow: 2,
                    gridColumn: 2,
                    cursor: "pointer",
                    alignSelf: "center",
                  }}
                >
                  <img
                    alt="remove"
                    src={Icons.trashcanRed}
                    style={{
                      width: "20px",
                      height: "20px",
                      marginLeft: "6px",
                      cursor: "pointer",
                    }}
                  ></img>
                </div>
              )}
            </Form.Group>
          </Col>
          <Col sm={4}>
            <Form.Group className="mb-3" controlId={`onsitePpaLength-${index}`}>
              <Form.Label>
                {site.tariffConfig.onsitePPA?.ppaPurchaseModel !==
                  "fixed_lease" && <>On-site PPA length (years)</>}
                {site.tariffConfig.onsitePPA?.ppaPurchaseModel ===
                  "fixed_lease" && <>PV lease length (years)</>}
              </Form.Label>
              <Form.Control
                type="text"
                value={
                  parseFloat(site.totalInstalledCapacity as string) === 0
                    ? ""
                    : ppa.ppaLength
                }
                onChange={(e) =>
                  setSite({
                    tariffConfig: {
                      onsitePPA: ppaUpdate(index, {
                        ppaLength: e.target.value,
                      }),
                    },
                  })
                }
                isInvalid={
                  !!errors[
                    `tariffConfig.onsitePPA${ppaErrorString(index)}.ppaLength`
                  ]
                }
                disabled={
                  parseFloat(site.totalInstalledCapacity as string) === 0
                }
              />
              <Form.Control.Feedback type="invalid">
                {
                  errors[
                    `tariffConfig.onsitePPA${ppaErrorString(index)}.ppaLength`
                  ]
                }
              </Form.Control.Feedback>
            </Form.Group>
          </Col>
          <Col sm={4}>
            <Form.Label>On-site PPA type</Form.Label>
            <Form
              style={{
                display: "flex",
                fontSize: 12,
                width: "100%",
                justifyContent: "space-between",
              }}
            >
              {Object.entries(PPA_TYPE_LABELS).map(([key, label]) => (
                <BorderedCheckbox
                  key={`onsite-${key}-${index}`}
                  dataTestId={`onsite-${key}-${index}`}
                  formName="ppa_type"
                  value={key}
                  label={label}
                  selectedOption={ppa.ppaType}
                  setSelectedOption={(value) =>
                    setSite({
                      tariffConfig: {
                        onsitePPA: ppaUpdate(index, {
                          ppaType: value,
                          ppaIndex: value === "fixed" ? 0 : ppa.ppaIndex,
                        }),
                      },
                    })
                  }
                  disabled={
                    parseFloat(site.totalInstalledCapacity as string) === 0
                  }
                />
              ))}
            </Form>
          </Col>
          {ppa.ppaType === "indexed" && (
            <Col sm={4}>
              <Form.Group
                className="mb-3"
                controlId={`onsitePpaIndex-${index}`}
              >
                <Form.Label>On-site PPA index (%)</Form.Label>
                <FormInputPercentage
                  value={
                    parseFloat(site.totalInstalledCapacity as string) === 0
                      ? ""
                      : (ppa.ppaIndex ?? "")
                  }
                  onChange={(e) =>
                    setSite({
                      tariffConfig: {
                        onsitePPA: ppaUpdate(index, {
                          ppaIndex: e.target.value,
                        }),
                      },
                    })
                  }
                  disabled={
                    parseFloat(site.totalInstalledCapacity as string) === 0
                  }
                  errorKey={`tariffConfig.onsitePPA${ppaErrorString(index)}.ppaIndex`}
                />
              </Form.Group>
            </Col>
          )}
        </Row>
      ))}
      <Row className="d-flex flex-wrap">
        <Col sm={4}>
          <Form.Group className={"mb-3"} controlId="leaseLengthDate">
            <Form.Label>Tenant lease length</Form.Label>
            <Form.Control
              style={{
                color: site.leaseLengthDate ? "black" : "grey",
              }}
              type="date"
              onClick={(e) => {
                const element = e.target;
                if ("showPicker" in HTMLInputElement.prototype) {
                  // showPicker() is supported. Chrome 99+ and Safari 16+
                  // see https://caniuse.com/?search=showPicker
                  (element as any).showPicker();
                }
              }}
              value={site.leaseLengthDate || ""}
              onChange={(e) =>
                setSite({
                  leaseLengthDate: e.target.value || null,
                })
              }
              onBlur={(_) => {
                if (site.leaseLengthDate) {
                  const newSuggestedPpaLength = ppaTermFromLeaseLength(
                    site.leaseLengthDate
                  );
                  if (
                    parseAsNumber(site.tariffConfig.onsitePPA?.ppaLength) !==
                    newSuggestedPpaLength.suggestedPpaLength
                  ) {
                    setSuggestedOnsitePpaLength(newSuggestedPpaLength);
                  }
                }
              }}
            />
          </Form.Group>
        </Col>
        <Col sm={4}>
          <Form.Group className="mb-3">
            <Form.Label>Lease length (months)</Form.Label>
            <Form.Control
              disabled
              value={siteCalculations.leaseLengthMonths ?? ""}
            />
          </Form.Group>
        </Col>
      </Row>
      <CollapsibleCard
        title={"More contract terms"}
        className="mt-2"
        isBulkEdit={isBulkEdit}
        initialOpen={false}
      >
        <Row className="d-flex flex-wrap align-items-end">
          <Col sm={4}>
            <Form.Group className="mb-3" controlId="leaseRevenueShare">
              <Form.Label>Lease revenue share (%)</Form.Label>
              <FormInputPercentage
                value={site.landlordLeaseRevenueShare}
                onChange={(e) =>
                  setSite({
                    landlordLeaseRevenueShare: e.target.value,
                  })
                }
                errorKey={"landlordLeaseRevenueShare"}
              />
            </Form.Group>
          </Col>
          {(isBulkEdit || !isOwnerOccupied) && (
            <Col sm={4}>
              <Form.Group className="mb-3" controlId="licenseRevenueShare">
                <Form.Label>License revenue share (%)</Form.Label>
                <FormInputPercentage
                  value={site.landlordLicenseRevenueShare}
                  onChange={(e) =>
                    setSite({
                      landlordLicenseRevenueShare: e.target.value,
                    })
                  }
                  errorKey={"landlordLicenseRevenueShare"}
                />
              </Form.Group>
            </Col>
          )}
          {(isBulkEdit || isOwnerOccupied) && (
            <>
              <Col sm={4}>
                <Form.Group className="mb-3" controlId="ooLicenseRevenueShare">
                  <Form.Label>OwnOcc license revenue share (%)</Form.Label>
                  <FormInputPercentage
                    value={site.ownerOccupierLicenseRevenueShare}
                    onChange={(e) =>
                      setSite({
                        ownerOccupierLicenseRevenueShare: e.target.value,
                      })
                    }
                    errorKey={"ownerOccupierLicenseRevenueShare"}
                  />
                </Form.Group>
              </Col>
              <Col sm={4}>
                <Form.Group className="mb-3" controlId="ooLicenseSaasTariff">
                  <Form.Label>OwnOcc license saas tariff rate</Form.Label>
                  <Form.Control
                    type="text"
                    value={site.ownerOccupierLicenseInRangeSaasTariffRate}
                    onChange={(e) =>
                      setSite({
                        ownerOccupierLicenseInRangeSaasTariffRate:
                          e.target.value,
                      })
                    }
                    isInvalid={
                      !!errors["ownerOccupierLicenseInRangeSaasTariffRate"]
                    }
                  />
                  <Form.Control.Feedback type="invalid">
                    {errors["ownerOccupierLicenseInRangeSaasTariffRate"]}
                  </Form.Control.Feedback>
                </Form.Group>
              </Col>
            </>
          )}
          {(isBulkEdit || siteCalculations.totalBatterySummary) && (
            <>
              <Col sm={4}>
                <Form.Label>Enable tariff arbitrage</Form.Label>
                <Form
                  style={{
                    display: "flex",
                    fontSize: 12,
                    width: "100%",
                    justifyContent: "space-between",
                  }}
                >
                  <BorderedCheckbox
                    dataTestId={`arbitrage-true`}
                    formName="arbitrage"
                    value={"true"}
                    label={"True"}
                    selectedOption={
                      site.tariffConfig.arbitrageEnabled ? "true" : "false"
                    }
                    setSelectedOption={(value) =>
                      setSite({
                        tariffConfig: {
                          arbitrageEnabled: value === "true",
                        },
                      })
                    }
                  />
                  <BorderedCheckbox
                    dataTestId={`arbitrage-false`}
                    formName="arbitrage"
                    value={"false"}
                    label={"False"}
                    selectedOption={
                      site.tariffConfig.arbitrageEnabled ? "true" : "false"
                    }
                    setSelectedOption={(value) =>
                      setSite({
                        tariffConfig: {
                          arbitrageEnabled: value === "true",
                        },
                      })
                    }
                  />
                </Form>
              </Col>
              {(site.tariffConfig.arbitrageEnabled || isBulkEdit) && (
                <>
                  <Col sm={4}>
                    <Form.Group
                      className="mb-3"
                      controlId="tariffConfig.arbitrageLeaseRevShare"
                    >
                      <Form.Label>Arb. lease rev share (%)</Form.Label>
                      <FormInputPercentage
                        value={site.tariffConfig.arbitrageLeaseRevShare}
                        onChange={(e) =>
                          setSite({
                            tariffConfig: {
                              ...site.tariffConfig,
                              arbitrageLeaseRevShare: e.target.value,
                            },
                          })
                        }
                        errorKey={"tariffConfig.arbitrageLeaseRevShare"}
                      />
                    </Form.Group>
                  </Col>
                  <Col sm={4}>
                    <Form.Group
                      className="mb-3"
                      controlId="tariffConfig.arbitrageLicenseRevShare"
                    >
                      <Form.Label>Arb. license rev share (%)</Form.Label>
                      <FormInputPercentage
                        value={site.tariffConfig.arbitrageLicenseRevShare}
                        onChange={(e) =>
                          setSite({
                            tariffConfig: {
                              ...site.tariffConfig,
                              arbitrageLicenseRevShare: e.target.value,
                            },
                          })
                        }
                        errorKey={"tariffConfig.arbitrageLicenseRevShare"}
                      />
                    </Form.Group>
                  </Col>
                  <Col sm={4}>
                    <Form.Group
                      className="mb-3"
                      controlId="tariffConfig.arbitrageTenantShare"
                    >
                      <Form.Label>Energy arb. tenant split (%)</Form.Label>
                      <FormInputPercentage
                        value={site.tariffConfig.arbitrageTenantShare}
                        onChange={(e) =>
                          setSite({
                            tariffConfig: {
                              ...site.tariffConfig,
                              arbitrageTenantShare: e.target.value,
                            },
                          })
                        }
                        errorKey={"tariffConfig.arbitrageTenantShare"}
                      />
                    </Form.Group>
                  </Col>
                </>
              )}
            </>
          )}
          <Col sm={4}>
            <Form.Label>Take or pay</Form.Label>
            <Form
              style={{
                display: "flex",
                fontSize: 12,
                width: "100%",
                justifyContent: "space-between",
              }}
            >
              <BorderedCheckbox
                dataTestId={`take-or-pay-true`}
                formName="takeOrPay"
                value={"true"}
                label={"On"}
                selectedOption={
                  site.tariffConfig.takeOrPay ? "true" : "false"
                }
                setSelectedOption={(value) =>
                  setSite({
                    tariffConfig: {
                      takeOrPay: value === "true",
                      takeOrPayMinKwh:
                        value === "true"
                          ? site.tariffConfig.takeOrPayMinKwh
                          : 0,
                    },
                  })
                }
              />
              <BorderedCheckbox
                dataTestId={`take-or-pay-false`}
                formName="takeOrPay"
                value={"false"}
                label={"Off"}
                selectedOption={
                  site.tariffConfig.takeOrPay ? "true" : "false"
                }
                setSelectedOption={(value) =>
                  setSite({
                    tariffConfig: {
                      takeOrPay: value === "true",
                      takeOrPayMinKwh:
                        value === "true"
                          ? site.tariffConfig.takeOrPayMinKwh
                          : 0,
                    },
                  })
                }
              />
            </Form>
            {errors["tariffConfig.takeOrPay"] && (
              <div
                className="invalid-feedback d-block"
                data-testid="take-or-pay-error"
              >
                {errors["tariffConfig.takeOrPay"]}
              </div>
            )}
          </Col>
          {site.tariffConfig.takeOrPay && (
            <Col sm={4}>
              <Form.Group
                className="mb-3"
                controlId="tariffConfig.takeOrPayMinKwh"
              >
                <Form.Label>Take or pay min (kWh)</Form.Label>
                <Form.Control
                  type="text"
                  value={site.tariffConfig.takeOrPayMinKwh}
                  onChange={(e) =>
                    setSite({
                      tariffConfig: {
                        takeOrPayMinKwh: e.target.value,
                      },
                    })
                  }
                  isInvalid={!!errors["tariffConfig.takeOrPayMinKwh"]}
                />
                <Form.Control.Feedback type="invalid">
                  {errors["tariffConfig.takeOrPayMinKwh"]}
                </Form.Control.Feedback>
              </Form.Group>
            </Col>
          )}
          <Col sm={4}>
            <Form.Group className="mb-3" controlId="systemLifetimeYears">
              <Form.Label>System lifetime (years)</Form.Label>
              <Form.Control
                type="text"
                value={site.systemLifetimeYears}
                onChange={(e) =>
                  setSite({ systemLifetimeYears: e.target.value })
                }
                isInvalid={!!errors.systemLifetimeYears}
              />
              <Form.Control.Feedback type="invalid">
                {errors.systemLifetimeYears}
              </Form.Control.Feedback>
            </Form.Group>
          </Col>
          <Col sm={4}>
            <Form.Group className="mb-3" controlId="vacancyLossRate">
              <Form.Label>Vacancy loss (%)</Form.Label>
              <FormInputPercentage
                value={isOwnerOccupied ? "" : site.vacancyLossRate}
                onChange={(e) => setSite({ vacancyLossRate: e.target.value })}
                disabled={isOwnerOccupied}
                errorKey={"vacancyLossRate"}
              />
            </Form.Group>
          </Col>
          <Col sm={4}>
            <Form.Group className="mb-3" controlId="buildingProfile">
              <Form.Label>O&M cost application</Form.Label>
              <Form.Select
                value={isOwnerOccupied ? "post" : site.oAndMCostMode}
                onChange={(e) => {
                  setSite({ oAndMCostMode: e.target.value });
                  if (
                    e.target.value === "post" &&
                    (parseAsNumber(site.landlordLeaseRevenueShare) !== 0.1 ||
                      parseAsNumber(site.landlordLicenseRevenueShare) !== 0.8)
                  ) {
                    setPendingUpdateSite([
                      {
                        title: "Revenue share",
                        beforeDefaults: {
                          landlordLeaseRevenueShare: 0.2,
                          landlordLicenseRevenueShare: 0.7,
                        },
                        afterDefaults: {
                          landlordLeaseRevenueShare: 0.1,
                          landlordLicenseRevenueShare: 0.8,
                        },
                      },
                    ]);
                  } else if (
                    e.target.value === "pre" &&
                    (parseAsNumber(site.landlordLeaseRevenueShare) !== 0.2 ||
                      parseAsNumber(site.landlordLicenseRevenueShare) !== 0.7)
                  ) {
                    setPendingUpdateSite([
                      {
                        title: "Revenue share",
                        beforeDefaults: {
                          landlordLeaseRevenueShare: 0.1,
                          landlordLicenseRevenueShare: 0.8,
                        },
                        afterDefaults: {
                          landlordLeaseRevenueShare: 0.2,
                          landlordLicenseRevenueShare: 0.7,
                        },
                      },
                    ]);
                  }
                }}
                disabled={isOwnerOccupied}
              >
                <option value={"post"}>Post-rev share</option>
                <option value={"pre"}>Pre-rev share</option>
              </Form.Select>
            </Form.Group>
          </Col>
          <Col sm={4}>
            <Form.Group className="mb-3" controlId="energyPriceInflationRate">
              <Form.Label>Cross-PPA indexation (%)</Form.Label>
              <FormInputPercentage
                value={site.energyPriceInflationRate}
                onChange={(e) =>
                  setSite({ energyPriceInflationRate: e.target.value })
                }
                errorKey={"energyPriceInflationRate"}
              />
            </Form.Group>
          </Col>
          <Col sm={4}>
            <Form.Group className="mb-3" controlId="ppaCrossPpaIndexationType">
              <Form.Label>Cross-PPA indexation type</Form.Label>
              <Form.Select
                value={
                  site.tariffConfig.onsitePPA?.ppaCrossPpaIndexationType ??
                  "compound"
                }
                onChange={(e) =>
                  setSite({
                    tariffConfig: {
                      onsitePPA: { ppaCrossPpaIndexationType: e.target.value },
                    },
                  })
                }
              >
                <option value={"compound"}>Compound</option>
                <option value={"simple"}>Simple</option>
              </Form.Select>
            </Form.Group>
          </Col>
          <Col sm={4}>
            <Form.Group className="mb-3" controlId="ppaPurchaseModel">
              <Form.Label>On-site purchase model</Form.Label>
              <Form.Select
                value={
                  site.tariffConfig.onsitePPA?.ppaPurchaseModel ?? "as_produced"
                }
                onChange={(e) =>
                  setSite({
                    tariffConfig: {
                      onsitePPA: { ppaPurchaseModel: e.target.value },
                    },
                  })
                }
              >
                <option value={"as_produced"}>Pay as produced PPA</option>
                <option value={"fixed_lease"}>PV lease</option>
              </Form.Select>
            </Form.Group>
          </Col>
          {site.tariffConfig.onsitePPA?.ppaPurchaseModel === "fixed_lease" && (
            <Col sm={4}>
              <Form.Group className="mb-3" controlId="ppaLeaseBillingFrequency">
                <Form.Label>On-site billing frequency</Form.Label>
                <Form.Select
                  value={
                    site.tariffConfig.onsitePPA?.ppaLeaseBillingFrequency ??
                    "quarterly"
                  }
                  onChange={(e) =>
                    setSite({
                      tariffConfig: {
                        onsitePPA: { ppaLeaseBillingFrequency: e.target.value },
                      },
                    })
                  }
                >
                  <option value={"monthly"}>Monthly</option>
                  <option value={"quarterly"}>Quarterly</option>
                  <option value={"annually"}>Annually</option>
                </Form.Select>
              </Form.Group>
            </Col>
          )}
        </Row>
      </CollapsibleCard>
      <SiteInvoiceProps site={site} setSite={setSite} />
      {suggestedOnsitePpaLength && (
        <Modal>
          <ModalView
            title={
              <>
                Set the On-site PPA length to{" "}
                {suggestedOnsitePpaLength.suggestedPpaLength} years for this{" "}
                {(suggestedOnsitePpaLength.leaseMonthsLeft / 12).toFixed(1)}{" "}
                year{" "}
                <span style={{ whiteSpace: "nowrap" }}>
                  ({suggestedOnsitePpaLength.leaseMonthsLeft} month)
                </span>{" "}
                lease length?
              </>
            }
            titlePadding="0"
            fontWeight="500"
            fontSize="18px"
            centerTitle={true}
            width="500px"
          >
            <Row style={{ marginTop: "10px", justifyContent: "center" }}>
              <Col md={4}>
                <Button
                  variant="secondary"
                  onClick={() => setSuggestedOnsitePpaLength(undefined)}
                >
                  No, keep at {site.tariffConfig.onsitePPA?.ppaLength}
                </Button>
              </Col>
              <Col md={4}>
                <Button
                  variant="success"
                  onClick={() => {
                    setSite({
                      tariffConfig: {
                        onsitePPA: {
                          ppaLength:
                            suggestedOnsitePpaLength.suggestedPpaLength,
                        },
                      },
                    });
                    setSuggestedOnsitePpaLength(undefined);
                  }}
                  style={{ float: "right" }}
                >
                  Yes, set to {suggestedOnsitePpaLength.suggestedPpaLength}
                </Button>
              </Col>
            </Row>
          </ModalView>
        </Modal>
      )}
    </CollapsibleCard>
  );
};

export default DealTerms;
